import React, { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, WifiOff } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isDeviceNotFound: boolean;
}

class ConvexErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isDeviceNotFound: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a "Device not found" error
    const isDeviceNotFound = error.message?.includes('Device not found');
    
    return { 
      hasError: true, 
      error,
      isDeviceNotFound
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ConvexErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isDeviceNotFound: false });
  };

  render() {
    if (this.state.hasError) {
      // For "Device not found" errors, show a friendly message
      if (this.state.isDeviceNotFound) {
        return (
          <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-6">
            <div className="text-center space-y-4 max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-muted flex items-center justify-center">
                <WifiOff className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Device Not Recognized</h2>
                <p className="text-sm text-muted-foreground">
                  This device hasn't been registered yet. Please ensure your device is properly configured.
                </p>
              </div>
              <Button onClick={this.handleRetry} variant="outline" className="mt-4">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        );
      }

      // For other errors, show generic error UI or use custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-6">
          <div className="text-center space-y-4 max-w-sm">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-destructive/10 flex items-center justify-center">
              <span className="text-destructive text-3xl font-bold">!</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
            <Button onClick={this.handleRetry} variant="outline" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ConvexErrorBoundary;
